import { JoinAdvancedFilterModel, NumberAdvancedFilterModel, ScalarAdvancedFilterModelType } from 'ag-grid-enterprise';

import { InputLabel, InputNumber, InputPanel } from '@camelot/form-model';
import { isNonNullable } from '@camelot/utils';

import { BaseCol, IInputOptions } from '../base-col';
import { NumberEditorComponent } from './number-editor.component';
import { NumberFilterComponent } from './number-filter.component';

type FilterTyping = JoinAdvancedFilterModel | NumberAdvancedFilterModel;
export class NumberCol extends BaseCol<FilterTyping> {
  public override getColDef() {
    return {
      ...super.getColDef(),
      ...{
        cellEditor: NumberEditorComponent,
        cellStyle: { justifyContent: 'flex-end', textAlign: 'right' },
        filter: NumberFilterComponent,
      },
    };
  }

  public override getInputForm(options?: IInputOptions) {
    if (options?.fromEdit) {
      return new InputNumber({
        key: this.key,
        value: options?.value?.toString(),
      });
    }
    const max = this._extractFiltersByFilterType(this.filterInstance, ['lessThan', 'lessThanOrEqual'])
      .filter(cond => cond.filterType === 'number')
      .map((cond: any) => cond.filter)
      .filter(isNonNullable)[0];
    const min = this._extractFiltersByFilterType(this.filterInstance, ['greaterThan', 'greaterThanOrEqual'])
      .filter(cond => cond.filterType === 'number')
      .map((cond: any) => cond.filter)
      .filter(isNonNullable)[0];

    return new InputPanel({
      key: 'number-panel',
      contentClass: 'row g-0',
      children: [
        ...(options?.fromView
          ? []
          : [
              new InputLabel({
                class: 'col-12',
                color: 'brand',
                key: 'number-panel-label',
                label: this.inputLabel,
                size: 'sm',
              }),
            ]),
        new InputNumber({
          key: this.key + '#min',
          label: 'min',
          class: options?.fromView ? 'col-12' : 'col-6 pe-1',
          value: min?.toString(),
        }),
        new InputNumber({
          key: this.key + '#max',
          label: 'max',
          class: options?.fromView ? 'col-12' : 'col-6 ps-1',
          value: max?.toString(),
        }),
      ],
    });
  }
  public override getFiltersModel(data: any): FilterTyping | null {
    const keys = Object.keys(data).filter(key => key.startsWith(this.key));

    if (keys.length === 0) {
      return null;
    }
    const filters = keys.reduce<NumberAdvancedFilterModel[]>((acc, key) => {
      const type = key.split('#')[1] || 'min';
      const rawValue = data[key];
      const value = Number(typeof rawValue === 'string' ? rawValue.replace(',', '.') : rawValue) ?? null;

      if (value) {
        acc.push({
          filterType: 'number',
          colId: this.key,
          type: this._getTypeOperator(type),
          filter: value,
        });
      }
      return acc;
    }, []);

    if (filters.length === 0) {
      return null;
    }
    if (filters.length === 1) {
      return filters[0];
    }
    return {
      filterType: 'join',
      type: 'AND',
      conditions: filters,
    };
  }

  private _getTypeOperator(type: string): ScalarAdvancedFilterModelType {
    if (type === 'max') {
      return 'lessThanOrEqual';
    }
    if (type === 'min') {
      return 'greaterThanOrEqual';
    }
    return 'notBlank';
  }
}
